/**
 * EditPersonDrawer — 560 px right slide-in for editing a single Person.
 *
 * Source: design_handoff_tank brief (Edit Person drawer principle #7).
 *
 * Tabs: Info / Aliases / Faces / Merge (with counts).
 * Chrome: 3 px phos left-bar in header, eyebrow row, title block, close X,
 * tab strip flush below header, persistent footer (Delete · ESC · Cancel ·
 * Save changes ⌘↵).
 *
 * The Info tab uses a dirty-buffer + Save-changes button. Aliases / Merge
 * commit immediately. Faces is a read-only list pending backend support.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  XMarkIcon,
  UserCircleIcon,
  TagIcon,
  FaceSmileIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';

import {
  searchTeams,
  searchPersons,
  mergePersons,
  fetchFacesByPerson,
} from '../../../../api';
import {
  Btn,
  Kbd,
  Mono,
  StatusPill,
  TabBar,
} from '../../../../shared/components/redesign';
import ConfirmDialog from '../../../../shared/components/ui/ConfirmDialog';

import styles from './EditPersonDrawer.module.css';

const TAB_ICON_PROPS = {
  width: 14,
  height: 14,
  strokeWidth: 1.75,
};

const TABS = [
  { key: 'info', label: 'Info', icon: <UserCircleIcon {...TAB_ICON_PROPS} /> },
  { key: 'aliases', label: 'Aliases', icon: <TagIcon {...TAB_ICON_PROPS} /> },
  { key: 'faces', label: 'Faces', icon: <FaceSmileIcon {...TAB_ICON_PROPS} /> },
  { key: 'merge', label: 'Merge', icon: <ArrowsRightLeftIcon {...TAB_ICON_PROPS} /> },
];

export default function EditPersonDrawer({
  person,
  isNew = false,
  onSave,
  onDelete,
  onClose,
}) {
  const drawerRef = useRef(null);
  const [activeTab, setActiveTab] = useState('info');
  const [confirm, setConfirm] = useState(null);

  // ── Info tab — dirty-buffer form ──────────────────────────────────────────
  const firstMembership = person?.team_memberships?.[0];
  const initialForm = useMemo(
    () => ({
      display_name: person?.display_name || '',
      first_name: person?.first_name || '',
      last_name: person?.last_name || '',
      jersey_number: person?.jersey_number || '',
      notes: person?.notes || '',
      team_id: firstMembership?.team_id || '',
    }),
    [person?.id], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [form, setForm] = useState(initialForm);
  const [savingInfo, setSavingInfo] = useState(false);
  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initialForm),
    [form, initialForm],
  );

  // Team search.
  const [teamSearchQuery, setTeamSearchQuery] = useState(
    firstMembership?.team_name || '',
  );
  const [teamSuggestions, setTeamSuggestions] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(
    firstMembership
      ? { id: firstMembership.team_id, name: firstMembership.team_name }
      : null,
  );
  useEffect(() => {
    if (selectedTeam && teamSearchQuery === selectedTeam.name) {
      setTeamSuggestions([]);
      return;
    }
    if (!teamSearchQuery || teamSearchQuery.length < 2) {
      setTeamSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await searchTeams(teamSearchQuery);
        setTeamSuggestions(res || []);
      } catch (e) {
        console.error(e);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [teamSearchQuery, selectedTeam]);

  const selectTeam = (team) => {
    setSelectedTeam(team);
    setTeamSearchQuery(team.name);
    setTeamSuggestions([]);
    setForm((prev) => ({ ...prev, team_id: team.id }));
  };
  const clearTeam = () => {
    setSelectedTeam(null);
    setTeamSearchQuery('');
    setTeamSuggestions([]);
    setForm((prev) => ({ ...prev, team_id: '' }));
  };

  const handleSaveInfo = useCallback(async () => {
    if (!form.display_name.trim()) return;
    setSavingInfo(true);
    try {
      await onSave(form);
    } finally {
      setSavingInfo(false);
    }
  }, [form, onSave]);

  // ── Aliases tab — immediate save ──────────────────────────────────────────
  const [aliases, setAliases] = useState(person?.aliases || []);
  const [aliasInput, setAliasInput] = useState('');
  const addAlias = useCallback(async () => {
    const v = aliasInput.trim();
    if (!v || aliases.includes(v)) return;
    const next = [...aliases, v];
    setAliases(next);
    setAliasInput('');
    try {
      await onSave({ ...form, aliases: next });
    } catch (e) {
      // Revert on failure.
      setAliases(aliases);
      console.error(e);
    }
  }, [aliasInput, aliases, form, onSave]);
  const removeAlias = useCallback(
    async (a) => {
      const next = aliases.filter((x) => x !== a);
      setAliases(next);
      try {
        await onSave({ ...form, aliases: next });
      } catch (e) {
        setAliases(aliases);
        console.error(e);
      }
    },
    [aliases, form, onSave],
  );

  // ── Merge tab ─────────────────────────────────────────────────────────────
  const [mergeQuery, setMergeQuery] = useState('');
  const [mergeSuggestions, setMergeSuggestions] = useState([]);
  useEffect(() => {
    if (!mergeQuery.trim()) {
      setMergeSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await searchPersons(mergeQuery);
        const list = res?.results || res || [];
        setMergeSuggestions(list.filter((p) => p.id !== person?.id));
      } catch (e) {
        console.error(e);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [mergeQuery, person?.id]);

  const handleMerge = useCallback(
    (target) => {
      setConfirm({
        title: `Merge into ${target.display_name}?`,
        message: `All faces and matches currently linked to "${person?.display_name}" will be reassigned to "${target.display_name}". This person will be deleted.`,
        confirmLabel: 'Merge',
        onConfirm: async () => {
          try {
            await mergePersons({
              source_id: person.id,
              target_id: target.id,
            });
            onClose?.();
          } catch (e) {
            console.error('Merge failed:', e);
          }
        },
      });
    },
    [person?.id, person?.display_name, onClose],
  );

  // ── Keyboard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      // Ignore when ConfirmDialog is open.
      if (document.querySelector('[role="alertdialog"]')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (activeTab === 'info' && dirty && !savingInfo) handleSaveInfo();
      } else if (!e.metaKey && !e.ctrlKey && !e.altKey) {
        const inField =
          e.target?.tagName === 'INPUT' ||
          e.target?.tagName === 'TEXTAREA' ||
          e.target?.isContentEditable;
        if (inField) return;
        const idx = ['1', '2', '3', '4'].indexOf(e.key);
        if (idx !== -1) {
          e.preventDefault();
          setActiveTab(TABS[idx].key);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeTab, dirty, savingInfo, handleSaveInfo, onClose]);

  // Close on click outside.
  useEffect(() => {
    const onDown = (e) => {
      if (drawerRef.current?.contains(e.target)) return;
      if (e.target.closest?.('[role="alertdialog"]')) return;
      onClose?.();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [onClose]);

  if (!person && !isNew) return null;

  const teamName =
    selectedTeam?.name ||
    person?.team_memberships?.[0]?.team_name ||
    'No team';
  const faceCount = person?.face_count ?? 0;

  return (
    <>
      <div className={styles.backdrop} aria-hidden="true" />
      <aside
        className={styles.drawer}
        ref={drawerRef}
        role="dialog"
        aria-label={isNew ? 'Create person' : 'Edit person'}
      >
        {/* Header — phos 3 px left-bar + eyebrow + title + close X. */}
        <div className={styles.header}>
          <div className={styles.headerBar} aria-hidden="true" />
          <div className={styles.headerText}>
            <Mono className={styles.eyebrow}>
              § PERSON{teamName ? ` · ${teamName.toUpperCase()}` : ''}
            </Mono>
            <h2 className={styles.title}>
              {isNew
                ? 'New person'
                : person?.display_name ||
                  `${person?.first_name || ''} ${person?.last_name || ''}`.trim() ||
                  `Person ${person?.id}`}
            </h2>
            {!isNew && person && (
              <Mono className={styles.subtitle}>
                {[
                  person.first_name,
                  person.last_name,
                  person.jersey_number ? `#${person.jersey_number}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Mono>
            )}
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <XMarkIcon style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Tabs — shared TabBar pill primitive. */}
        <div className={styles.tabsWrapper}>
          <TabBar
            active={activeTab}
            onChange={setActiveTab}
            tabs={TABS.map((t) => {
              const count =
                t.key === 'aliases'
                  ? aliases.length
                  : t.key === 'faces'
                    ? faceCount
                    : undefined;
              return {
                id: t.key,
                label: t.label,
                icon: t.icon,
                count: count > 0 ? count : undefined,
              };
            })}
          />
        </div>

        {/* Body */}
        <div className={styles.body}>
          {activeTab === 'info' && (
            <div className={styles.section}>
              <Mono className={styles.sectionEyebrow}>Identity</Mono>
              <div className={styles.formGroup}>
                <label className={styles.label}>Display name</label>
                <input
                  type="text"
                  className={styles.input}
                  value={form.display_name}
                  onChange={(e) =>
                    setForm({ ...form, display_name: e.target.value })
                  }
                  placeholder="Full display name"
                />
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>First name</label>
                  <input
                    type="text"
                    className={styles.input}
                    value={form.first_name}
                    onChange={(e) =>
                      setForm({ ...form, first_name: e.target.value })
                    }
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Last name</label>
                  <input
                    type="text"
                    className={styles.input}
                    value={form.last_name}
                    onChange={(e) =>
                      setForm({ ...form, last_name: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Team</label>
                  <div className={styles.teamSearchContainer}>
                    <div className={styles.teamInputWrapper}>
                      <input
                        type="text"
                        className={styles.input}
                        placeholder="Search teams…"
                        value={teamSearchQuery}
                        onChange={(e) => setTeamSearchQuery(e.target.value)}
                        autoComplete="off"
                      />
                      {selectedTeam && (
                        <button
                          type="button"
                          className={styles.clearTeamBtn}
                          onClick={clearTeam}
                          aria-label="Clear team"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    {teamSuggestions.length > 0 && (
                      <div className={styles.suggestions}>
                        {teamSuggestions.map((team) => (
                          <button
                            key={team.id}
                            type="button"
                            className={styles.suggestionItem}
                            onClick={() => selectTeam(team)}
                          >
                            <span>{team.name}</span>
                            {team.team_type && (
                              <Mono className={styles.suggestionMeta}>
                                {team.team_type}
                              </Mono>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Jersey #</label>
                  <input
                    type="number"
                    className={styles.input}
                    value={form.jersey_number || ''}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        jersey_number: e.target.value
                          ? parseInt(e.target.value, 10)
                          : '',
                      })
                    }
                    min={0}
                    max={999}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Notes</label>
                <textarea
                  className={styles.textarea}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
          )}

          {activeTab === 'aliases' && (
            <div className={styles.section}>
              <Mono className={styles.sectionEyebrow}>
                Aliases · {aliases.length}
              </Mono>
              <div className={styles.aliasInputRow}>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Add alias…"
                  value={aliasInput}
                  onChange={(e) => setAliasInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addAlias();
                    }
                  }}
                />
                <Btn kind="primary" size="sm" onClick={addAlias}>
                  Add
                </Btn>
              </div>
              {aliases.length === 0 ? (
                <p className={styles.emptyNote}>No aliases yet.</p>
              ) : (
                <ul className={styles.aliasList}>
                  {aliases.map((a) => (
                    <li key={a} className={styles.aliasRow}>
                      <span>{a}</span>
                      <button
                        type="button"
                        className={styles.aliasRemove}
                        onClick={() => removeAlias(a)}
                        aria-label={`Remove ${a}`}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {activeTab === 'faces' && (
            <PersonFacesTab personId={person?.id} faceCount={faceCount} />
          )}

          {activeTab === 'merge' && (
            <div className={styles.section}>
              <Mono className={styles.sectionEyebrow}>Merge into another person</Mono>
              <p className={styles.helpText}>
                Pick a target person. All faces and match assignments linked
                to <strong>{person?.display_name}</strong> will move to the
                target. <strong>{person?.display_name}</strong> will be
                deleted.
              </p>
              <input
                type="text"
                className={styles.input}
                placeholder="Search persons…"
                value={mergeQuery}
                onChange={(e) => setMergeQuery(e.target.value)}
              />
              {mergeSuggestions.length > 0 && (
                <ul className={styles.aliasList}>
                  {mergeSuggestions.slice(0, 10).map((p) => (
                    <li key={p.id} className={styles.aliasRow}>
                      <span>{p.display_name}</span>
                      <Btn
                        kind="primary"
                        size="sm"
                        onClick={() => handleMerge(p)}
                      >
                        Merge into this
                      </Btn>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Footer — Delete (left) · ESC TO CLOSE · Cancel · Save changes ⌘↵ */}
        <div className={styles.footer}>
          {!isNew && onDelete && (
            <Btn
              kind="danger"
              size="sm"
              onClick={() =>
                setConfirm({
                  title: `Delete ${person?.display_name || 'person'}?`,
                  message:
                    'This permanently removes the person. Linked faces become unlinked.',
                  confirmLabel: 'Delete',
                  onConfirm: () => onDelete(person),
                })
              }
            >
              Delete
            </Btn>
          )}
          <span className={styles.escHint}>
            <Kbd>ESC</Kbd> to close
          </span>
          <span className={styles.spacer} />
          <Btn kind="ghost" size="sm" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            kind="primary"
            size="sm"
            onClick={handleSaveInfo}
            disabled={savingInfo || !dirty || !form.display_name.trim()}
          >
            {savingInfo ? (
              'Saving…'
            ) : (
              <>
                Save changes <Kbd>⌘↵</Kbd>
              </>
            )}
          </Btn>
        </div>
      </aside>

      <ConfirmDialog
        isOpen={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        onConfirm={() => {
          confirm?.onConfirm?.();
          setConfirm(null);
        }}
        onCancel={() => setConfirm(null)}
      />
    </>
  );
}

// Lazy face thumbs for the Faces tab. Faces below the slider's quality
// threshold stay in the grid (per design) but render dimmed + greyscale
// + with a "BLUR" corner badge so the user can still see them at a glance.
function PersonFacesTab({ personId, faceCount }) {
  const [faces, setFaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Default: hide only very blurry per the design choice. Reading the
  // raw 0..1 quality value; faces with quality below this get the
  // below-threshold visual treatment.
  const [threshold, setThreshold] = useState(0.1);

  useEffect(() => {
    if (!personId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchFacesByPerson(personId)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data?.results || [];
        setFaces(list);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || 'Failed to load faces');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [personId]);

  const visibleCount = useMemo(
    () => faces.filter((f) => (f.embedding_quality ?? 0) >= threshold).length,
    [faces, threshold],
  );

  return (
    <div className={styles.section}>
      <Mono className={styles.sectionEyebrow}>
        Faces · {faceCount}
      </Mono>

      {personId && (
        <div className={styles.facesToolbar}>
          <label className={styles.facesSliderLabel}>
            <span>Hide below quality</span>
            <Mono className={styles.facesThreshold}>{threshold.toFixed(2)}</Mono>
          </label>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={threshold}
            onChange={(e) => setThreshold(parseFloat(e.target.value))}
            className={styles.facesSlider}
            aria-label="Hide blurry faces below quality threshold"
          />
          <Mono className={styles.facesCounts}>
            {visibleCount} / {faces.length} visible
          </Mono>
        </div>
      )}

      {loading && <p className={styles.emptyNote}>Loading faces…</p>}
      {error && <p className={styles.emptyNote}>Could not load faces: {error}</p>}
      {!loading && !error && faces.length === 0 && (
        <p className={styles.emptyNote}>No faces assigned yet.</p>
      )}

      {faces.length > 0 && (
        <div className={styles.facesGrid}>
          {faces.map((face) => {
            const quality = face.embedding_quality ?? 0;
            const dim = quality < threshold;
            return (
              <div
                key={face.id}
                className={clsx(styles.faceCard, dim && styles.faceCardDim)}
                title={`Quality: ${quality.toFixed(3)}`}
              >
                {face.preview_url ? (
                  <img
                    src={face.preview_url}
                    alt=""
                    className={styles.faceThumb}
                    loading="lazy"
                  />
                ) : (
                  <div className={styles.faceThumbPlaceholder} />
                )}
                {dim && <span className={styles.faceBlurBadge}>BLUR</span>}
                <Mono className={styles.faceQuality}>{quality.toFixed(2)}</Mono>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
